import { ElementButton, InputText } from 'macaron-frontend-ui'
import { useErrorOnBlur } from '~/features/reservationInput/hooks/useErrorOnBlur'
import { sva } from '~/styled-system/css'
import { useDebouncedOnChange } from '../../hooks/useDebounce'
import { placeholder } from '../../lib/placeholder'
import { useLoginProps } from '../../store/reserve'
import { useReserverPhoneNumber } from '../../store/stateMachine/context'
import {
  useIsPhoneNumberRegistered,
  useReserverPhoneNumberError,
  useSetReserverPhoneNumber,
} from '../../store/userInput'
import { AlreadyRegisteredMemberAlert } from './AlreadyRegisteredMemberAlert'

const phoneNumberStyles = sva({
  slots: ['container', 'heading', 'title', 'required', 'note', 'content'],
  base: {
    container: {
      spaceY: '1',
      md: {
        display: 'flex',
        alignItems: 'center',
        spaceY: '0',
        spaceX: '2',
      },
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      spaceX: '2',
      md: {
        display: 'block',
        spaceX: '0',
        width: '[188px]',
        flexShrink: '0',
        spaceY: '1',
      },
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 'md',
    },
    required: {
      ml: '1',
      fontSize: 'sm',
      color: 'accent.pink',
    },
    note: {
      fontSize: 'xs',
    },
    content: {
      width: 'full',
      spaceY: '1',
    },
  },
})

export function PhoneNumber({ children }: React.PropsWithChildren) {
  const classes = phoneNumberStyles()
  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <p className={classes.title}>
          携帯電話
          <span className={classes.required}>*</span>
        </p>
        <p className={classes.note}>※ハイフン不要</p>
      </div>
      <div className={classes.content}>{children}</div>
    </div>
  )
}

const phoneNumberInputStyles = sva({
  slots: ['container', 'note', 'alert'],
  base: {
    container: {
      width: 'full',
      spaceY: '1',
    },
    note: {
      mt: '1',
      fontSize: 'xs',
      color: 'gray.700',
    },
    alert: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      spaceX: '2',
      rounded: 'default',
      bg: 'gray.100',
      p: '2',
    },
  },
})

export function PhoneNumberInput() {
  const { onChange, error, phoneNumber, registered, onOpen, onBlur } = usePhoneNumberInput()
  const classes = phoneNumberInputStyles()
  return (
    <div className={classes.container}>
      <InputText
        errorMessage={error}
        width={300}
        placeholder={placeholder.phoneNumber}
        withSpread={true}
        type="tel"
        onChange={onChange}
        onBlur={onBlur}
        defaultValue={phoneNumber}
      />
      <p className={classes.note}>※携帯電話をお持ちではない場合、固定電話番号を入力ください。</p>
      {/* 会員の存在確認してよいのは一休だけ  */}
      {import.meta.env.VITE_MODE === 'ikyu' && registered && (
        <div className={classes.alert}>
          <AlreadyRegisteredMemberAlert
            phoneNumberOrEmail={phoneNumber}
            type="phoneNumber"
            registered={registered}
          />
          <ElementButton label="ログイン" color="login" size="xSmall" onClick={onOpen(false)} />
        </div>
      )}
    </div>
  )
}

function usePhoneNumberInput() {
  const phoneNumber = useReserverPhoneNumber()
  const registered = useIsPhoneNumberRegistered()
  const change = useSetReserverPhoneNumber()
  const onChange = useDebouncedOnChange(change, phoneNumber)
  const phoneNumberError = useReserverPhoneNumberError()
  const { onOpen } = useLoginProps()
  const { error, onBlur } = useErrorOnBlur(phoneNumberError)
  return { onChange, error, phoneNumber, registered, onOpen, onBlur }
}
